import React, { useState, useEffect } from "react";
import { useMiniERP } from "./hooks/useMiniERP";
import { useStatusBanner, StatusBanner } from "./components/MiniERPStatusBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faToggleOn,
  faToggleOff,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const MiniERPLedger = () => {
  const {
    fetchLedgers,
    createLedger,
    updateLedger,
    toggleLedger,
    seedDefaultLedgers,
  } = useMiniERP();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingLedger, setEditingLedger] = useState(null);
  const [ledgersList, setLedgersList] = useState([]);
  const { status, showSuccess, showError } = useStatusBanner();
  const [listStatus, setListStatus] = useState(null);

  const [formData, setFormData] = useState({
    ledgerCode: "",
    name: "",
    group: "Balance Sheet",
    subgroup: "",
    openingBalance: 0,
    balanceType: "Debit",
    isActive: true,
  });

  // Load Ledgers
  useEffect(() => {
    const loadLedgers = async () => {
      try {
        const res = await fetchLedgers();
        setLedgersList(res?.data || []);
      } catch (err) {
        console.error("Failed to load ledgers", err);
      }
    };
    loadLedgers();
  }, []);

  const filteredLedgers = ledgersList.filter((ledger) => {
    const matchesSearch =
      ledger.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ledger.ledgerCode?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup =
      selectedGroup === "All" || ledger.group === selectedGroup;
    return matchesSearch && matchesGroup;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLedger) {
        await updateLedger(editingLedger._id, formData);
        showSuccess("Ledger updated successfully!");
      } else {
        await createLedger(formData);
        showSuccess("Ledger created successfully!");
      }

      // Refresh list
      const res = await fetchLedgers();
      setLedgersList(res?.data || []);
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleLedger(id);
      const res = await fetchLedgers();
      setLedgersList(res?.data || []);
      setListStatus({ type: 'success', message: 'Status updated.' });
      setTimeout(() => setListStatus(null), 2500);
    } catch (err) {
      setListStatus({ type: 'error', message: 'Toggle failed: ' + err.message });
    }
  };

  const openEditModal = (ledger) => {
    setEditingLedger(ledger);
    setFormData({
      ledgerCode: ledger.ledgerCode,
      name: ledger.name,
      group: ledger.group,
      subgroup: ledger.subgroup || "",
      openingBalance: ledger.openingBalance || 0,
      balanceType: ledger.balanceType || "Debit",
      isActive: ledger.isActive,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      ledgerCode: "",
      name: "",
      group: "Balance Sheet",
      subgroup: "",
      openingBalance: 0,
      balanceType: "Debit",
      isActive: true,
    });
    setEditingLedger(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-cyan-200 text-left items-start">Ledger Master</h1>
            <p className="text-gray-400">
              Manage Chart of Accounts (Balance Sheet & P&amp;L)
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="mt-4 md:mt-0 bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-xl flex items-center gap-2 font-medium"
          >
            <FontAwesomeIcon icon={faPlus} /> New Ledger
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-3.5 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 pl-11 py-3 rounded-xl text-gray-200"
            />
          </div>

          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="bg-gray-700 border border-gray-600 px-2 py-1 rounded-xl text-gray-200"
          >
            <option value="All">All Groups</option>
            <option value="Balance Sheet">Balance Sheet</option>
            <option value="Profit & Loss">Profit & Loss</option>
          </select>
        </div>

        {/* Ledger List */}
        {listStatus && <div className="mb-4"><StatusBanner status={listStatus} /></div>}
        <div className="bg-gray-800 rounded-3xl overflow-hidden">
          {/* ================= MOBILE CARDS ================= */}
          <div className="block lg:hidden space-y-3">
            {filteredLedgers.map((ledger) => (
              <div
                key={ledger._id}
                className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-xs"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-cyan-300">
                      {ledger.ledgerCode}
                    </div>
                    <div className="text-sm font-semibold text-white">
                      {ledger.name}
                    </div>
                  </div>

                  <span
                    className={`px-2 py-1 rounded-full text-[11px] ${ledger.group === "Balance Sheet" ? "bg-blue-900 text-blue-300" : "bg-emerald-900 text-emerald-300"}`}
                  >
                    {ledger.group}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 text-gray-300">
                  <div>
                    <div className="text-gray-500">Subgroup</div>
                    <div>{ledger.subgroup || "-"}</div>
                  </div>

                  <div className="text-right">
                    <div className="text-gray-500">Opening</div>
                    <div className="font-semibold text-white">
                      ₹{ledger.openingBalance || 0}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">Type</div>
                    <span
                      className={`font-medium ${ledger.balanceType === "Debit" ? "text-red-400" : "text-green-400"}`}
                    >
                      {ledger.balanceType}
                    </span>
                  </div>

                  <div className="flex justify-end gap-2 items-end">
                    <button
                      onClick={() => handleToggle(ledger._id)}
                      className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                    >
                      <FontAwesomeIcon
                        icon={ledger.isActive ? faToggleOn : faToggleOff}
                      />
                    </button>

                    <button
                      onClick={() => openEditModal(ledger)}
                      className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">
                    Code
                  </th>
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">
                    Ledger Name
                  </th>
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">
                    Group
                  </th>
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">
                    Subgroup
                  </th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">
                    Opening Bal
                  </th>
                  <th className="text-center py-3 px-3 text-gray-400 font-medium">
                    Type
                  </th>
                  <th className="text-center py-3 px-3 text-gray-400 font-medium">
                    Status
                  </th>
                  <th className="text-center py-3 px-3 text-gray-400 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-700">
                {filteredLedgers.map((ledger) => (
                  <tr
                    key={ledger._id}
                    className="hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-3 px-3 font-mono text-cyan-300">
                      {ledger.ledgerCode}
                    </td>

                    <td className="py-3 px-3 font-medium">{ledger.name}</td>

                    <td className="py-3 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${ledger.group === "Balance Sheet" ? "bg-blue-900 text-blue-300" : "bg-emerald-900 text-emerald-300"}`}
                      >
                        {ledger.group}
                      </span>
                    </td>

                    <td className="py-3 px-3 text-gray-400">
                      {ledger.subgroup || "-"}
                    </td>

                    <td className="py-3 px-3 text-right font-medium">
                      ₹{ledger.openingBalance || 0}
                    </td>

                    <td className="py-3 px-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${ledger.balanceType === "Debit" ? "text-red-400" : "text-green-400"}`}
                      >
                        {ledger.balanceType}
                      </span>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <button
                        onClick={() => handleToggle(ledger._id)}
                        className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                      >
                        <FontAwesomeIcon
                          icon={ledger.isActive ? faToggleOn : faToggleOff}
                        />
                      </button>
                    </td>

                    <td className="py-3 px-3 text-center">
                      <button
                        onClick={() => openEditModal(ledger)}
                        className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredLedgers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No ledgers found. Click "New Ledger" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl w-full max-w-lg p-8">
            <h2 className="text-2xl font-bold text-cyan-200 mb-6">
              {editingLedger ? "Edit Ledger" : "Create New Ledger"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 mb-1">Ledger Code</label>
                <input
                  type="text"
                  value={formData.ledgerCode}
                  onChange={(e) =>
                    setFormData({ ...formData, ledgerCode: e.target.value })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  disabled={!!editingLedger}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Ledger Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1">Group</label>
                  <select
                    value={formData.group}
                    onChange={(e) =>
                      setFormData({ ...formData, group: e.target.value })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  >
                    <option value="Balance Sheet">Balance Sheet</option>
                    <option value="Profit & Loss">Profit & Loss</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 mb-1">
                    Balance Type
                  </label>
                  <select
                    value={formData.balanceType}
                    onChange={(e) =>
                      setFormData({ ...formData, balanceType: e.target.value })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  >
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-1">
                  Subgroup (Optional)
                </label>
                <input
                  type="text"
                  value={formData.subgroup}
                  onChange={(e) =>
                    setFormData({ ...formData, subgroup: e.target.value })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1">
                  Opening Balance
                </label>
                <input
                  type="number"
                  value={formData.openingBalance}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      openingBalance: Number(e.target.value),
                    })
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                />
              </div>

              <StatusBanner status={status} />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium"
                >
                  {editingLedger ? "Update Ledger" : "Create Ledger"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPLedger;